import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import type { UpdateClientRequest } from '@diana/contracts';
import { useDianaUIPortal } from '../../ui/index.js';

export type ClientDraft = Omit<UpdateClientRequest, 'expectedRevision'>;
export type ClientComparison = {
  current: ClientDraft;
  submitted: ClientDraft;
  localSubmitted?: ClientDraft;
};
const fields = [
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['instagram', 'Instagram'],
  ['notes', 'Notes'],
  ['archived', 'Archived'],
] as const;
export function clientDifferences(comparison: ClientComparison) {
  return fields.filter(
    ([key]) => (comparison.current[key] ?? null) !== (comparison.submitted[key] ?? null),
  );
}
const display = (value: string | boolean | null | undefined) =>
  typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value || 'Empty';

export function ClientConflictDialog({
  onRestoreFocus,
  open,
  comparison,
  latest,
  onAcceptServer,
  onReapplyLocal,
  onCopyLocal,
}: {
  onRestoreFocus?(): void;
  open: boolean;
  comparison: ClientComparison | null;
  latest: ClientDraft;
  onAcceptServer(): void;
  onReapplyLocal(): void;
  onCopyLocal(): Promise<void>;
}) {
  const portal = useDianaUIPortal();
  const [feedback, setFeedback] = useState('');
  const laterEdits =
    comparison &&
    fields.some(
      ([key]) =>
        (latest[key] ?? null) !==
        ((comparison.localSubmitted ?? comparison.submitted)[key] ?? null),
    );
  return (
    <Dialog.Root open={open}>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="client-conflict__overlay" />
        <Dialog.Content
          className="client-conflict"
          onCloseAutoFocus={(event) => {
            if (!onRestoreFocus) return;
            event.preventDefault();
            onRestoreFocus();
          }}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <p className="client-conflict__eyebrow">Revision conflict</p>
          <Dialog.Title>This client changed on another device</Dialog.Title>
          <Dialog.Description>
            Your local draft is still here. Use the server version, or replace it with your entire
            local draft.
          </Dialog.Description>
          {comparison ? (
            <div className="client-conflict__comparison">
              <div className="client-conflict__columns">
                <span>Newer version</span>
                <span>Submitted edit</span>
              </div>
              {clientDifferences(comparison).map(([key, label]) => (
                <section key={key} aria-label={`${label} difference`}>
                  <h3>{label}</h3>
                  <div className="client-conflict__columns">
                    <p>{display(comparison.current[key])}</p>
                    <p>{display(comparison.submitted[key])}</p>
                  </div>
                </section>
              ))}
              {clientDifferences(comparison).length === 0 ? (
                <p>The editable fields match.</p>
              ) : null}
            </div>
          ) : null}
          {laterEdits ? (
            <p>
              You continued editing after submitting. Copy and reapply include those later local
              edits too.
            </p>
          ) : null}
          <div className="client-conflict__actions">
            <button
              type="button"
              className="client-button"
              onClick={() => {
                void onCopyLocal().then(
                  () => setFeedback('Local draft copied'),
                  () => setFeedback('Could not copy local draft. Try again.'),
                );
              }}
            >
              Copy local text
            </button>
            <button type="button" className="client-button" onClick={onAcceptServer}>
              Use server version
            </button>
            <button
              type="button"
              className="client-button client-button--primary"
              onClick={onReapplyLocal}
            >
              Reapply my draft
            </button>
          </div>
          {feedback ? <p role="status">{feedback}</p> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
